import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Where the error happened — used for the "Back to" button route */
  fallbackRoute?: string;
  /** Label for the "Back to" button */
  fallbackLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] React render error caught:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleBackToDashboard = () => {
    const route = this.props.fallbackRoute ?? '/';
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(route, { replace: true });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#f3f7fb] flex items-center justify-center px-4">
          <div className="text-center max-w-md mx-auto">
            {/* Icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-rose-400 text-3xl"></i>
            </div>

            {/* Message */}
            <h1 className="text-slate-800 text-xl font-bold mb-2">
              Something went wrong
            </h1>
            <p className="text-slate-500 text-sm leading-relaxed mb-1">
              We couldn&apos;t load this section. Please try again.
            </p>
            {this.state.error && (
              <p className="text-slate-400 text-xs font-mono bg-slate-50 rounded-lg px-3 py-2 mt-3 mb-6 break-all border border-slate-100 max-h-20 overflow-y-auto">
                {this.state.error.message}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-sm"></i>
                Retry
              </button>
              <button
                onClick={this.handleBackToDashboard}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-sm"></i>
                {this.props.fallbackLabel ?? 'Back to dashboard'}
              </button>
            </div>

            {/* Subtle hint */}
            <p className="text-slate-400 text-[11px] mt-8">
              If the problem persists, please contact support or try refreshing the page.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}